// Customer Story — sticky sidebar (Figma 4501:12160).
// Scroll-nav + stats + a related-story card + an "Apply" CTA. The whole column
// is sticky and pins beside the (taller) article. Hidden below lg, where the
// stats + CTA are surfaced inline instead.

import { Button } from "@/components/ui/button";
import { Link } from "@/components/ui/link";
import type { CaseStudyStory } from "@/lib/content/schemas";

import { CustomerStoryFacts } from "./facts";
import type { CustomerStoryFactLabels } from "./facts";
import { CustomerStorySidebarNav } from "./sidebar-nav";
import { CustomerStorySidebarVideo } from "./sidebar-video";
import type { StoryNavItem } from "./use-active-section";

const SIGNUP_URL = "https://app.wayflyer.com/authn/signup";

interface CustomerStorySidebarProps {
  navItems: StoryNavItem[];
  navigationAriaLabel?: string;
  /** Customer facts block; omitted when the study supplies none of them. */
  factLabels?: CustomerStoryFactLabels;
  industry?: string;
  location?: string;
  websiteUrl?: string;
  stats?: CaseStudyStory["stats"];
  related?: CaseStudyStory["related"];
  apply?: CaseStudyStory["apply"];
  defaultApplyText?: string;
  defaultApplyLabel?: string;
  video?: {
    playbackId: string;
    title: string;
    playLabel?: string;
    thumbnailSrc?: string;
    thumbnailAlt?: string;
  };
}

export function CustomerStorySidebar({
  navItems,
  navigationAriaLabel,
  factLabels,
  industry,
  location,
  websiteUrl,
  stats,
  related,
  apply,
  defaultApplyText,
  defaultApplyLabel,
  video,
}: CustomerStorySidebarProps) {
  return (
    <aside className="border-grey-100 bg-grey-0 hidden w-[196px] shrink-0 border-b border-l lg:block">
      <div className="bg-grey-0 sticky top-[calc(var(--nav-offset)+16px)] flex flex-col self-start">
        <CustomerStorySidebarNav
          items={navItems}
          ariaLabel={navigationAriaLabel}
        />
        {factLabels ? (
          <CustomerStoryFacts
            labels={factLabels}
            industry={industry}
            location={location}
            websiteUrl={websiteUrl}
          />
        ) : null}
        {stats && stats.length > 0 ? <SidebarStats stats={stats} /> : null}
        {video || related ? (
          <StoryAsideBlock related={related} video={video} />
        ) : null}
        <ApplyCard
          apply={apply}
          defaultText={defaultApplyText}
          defaultLabel={defaultApplyLabel}
        />
      </div>
    </aside>
  );
}

function StoryAsideBlock({
  video,
  related,
}: {
  video?: CustomerStorySidebarProps["video"];
  related?: CaseStudyStory["related"];
}) {
  return (
    <div className="flex flex-col gap-3 p-3">
      {video ? (
        <CustomerStorySidebarVideo
          playbackId={video.playbackId}
          title={video.title}
          playLabel={video.playLabel}
          thumbnailSrc={video.thumbnailSrc}
          thumbnailAlt={video.thumbnailAlt}
        />
      ) : null}
      {related ? (
        <RelatedStoryCard related={related} showThumbnail={!video} />
      ) : null}
    </div>
  );
}

function SidebarStats({
  stats,
}: {
  stats: NonNullable<CaseStudyStory["stats"]>;
}) {
  return (
    <div className="flex flex-col gap-6 p-3">
      {stats.map((stat) => (
        <div key={stat.label} className="flex flex-col gap-2">
          <p className="text-ds-heading-h5 text-grey-950">{stat.value}</p>
          <p className="text-grey-600 text-[12px] leading-[1.4] tracking-[-0.24px] whitespace-pre-line">
            {stat.label}
          </p>
        </div>
      ))}
    </div>
  );
}

function RelatedStoryCard({
  related,
  showThumbnail = true,
}: {
  related: NonNullable<CaseStudyStory["related"]>;
  showThumbnail?: boolean;
}) {
  const href = `/our-customers/${related.slug}`;
  return (
    <div className="flex flex-col gap-3">
      {showThumbnail ? (
        <Link
          href={href}
          className="border-grey-100 group relative block aspect-[169/95] overflow-hidden rounded-lg border"
        >
          {related.thumbnail ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={related.thumbnail.src}
              alt={related.thumbnail.alt}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            />
          ) : (
            <span className="bg-grey-100 block h-full w-full" />
          )}
          <PlayBadge className="absolute right-1.5 bottom-1.5" />
        </Link>
      ) : null}
      <Link
        href={href}
        className="text-grey-600 text-[12px] leading-[1.4] tracking-[-0.24px] hover:opacity-70"
      >
        {related.label ? (
          <span className="text-grey-950">{related.label} </span>
        ) : null}
        {related.title}
      </Link>
    </div>
  );
}

function ApplyCard({
  apply,
  defaultText = "Apply in less than 10 minutes.",
  defaultLabel = "Apply now",
}: {
  apply?: CaseStudyStory["apply"];
  defaultText?: string;
  defaultLabel?: string;
}) {
  const text = apply?.text ?? defaultText;
  const label = apply?.label ?? defaultLabel;
  const href = apply?.href ?? SIGNUP_URL;
  const external = href.startsWith("http");
  return (
    <div className="flex flex-col gap-4 p-3">
      <p className="text-ds-heading-h5 text-grey-950">{text}</p>
      <Button
        size="sm"
        className="w-fit"
        render={<Link href={href} external={external} />}
      >
        {label}
      </Button>
    </div>
  );
}

function PlayBadge({ className }: { className?: string }) {
  return (
    <span className={className} aria-hidden style={{ display: "inline-flex" }}>
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="12" cy="12" r="12" fill="#0C0D0D" fillOpacity="0.6" />
        <path d="M10 8.5 16 12l-6 3.5v-7Z" fill="#fff" />
      </svg>
    </span>
  );
}
